import {
  CameraIntegration,
  contactSupportMessage,
  formatTitle,
  epochTimeOfSelectedEvent,
} from './base';
import dayjs from 'dayjs';
import { get, post } from '../../helpers/securityService';
import Bowser from 'bowser';

export class OpenEye extends CameraIntegration {
  constructor(camera, ...args) {
    super(camera, ...args);
    if (!camera.tenantId) throw new Error('camera.tenantId is required');

    this.cameraProviderName = 'OpenEye';
    this.cookieSet = false;
    this.settingCookie = null; // null | promise
    this.HLSSupport = false;
    this.MP4Support = false;
    this.checkBrowserSupport();
  }

  checkBrowserSupport() {
    const deviceInfo = Bowser.getParser(window.navigator.userAgent);
    const { browser, os } = Bowser.parse(window.navigator.userAgent);

    const isIOSmobleApp =
      (os.name.toLowerCase() === 'macos' || os.name.toLowerCase() === 'ios') &&
      browser.name.toLocaleLowerCase() === 'safari' &&
      !browser.version;

    const hlsSupportedBrowsers = deviceInfo.satisfies({
      macos: {
        safari: '>=14',
      },
      ios: {
        safari: '>=14',
        chrome: '>=26',
      },
    });

    if (hlsSupportedBrowsers || isIOSmobleApp) {
      this.HLSSupport = true;
    } else {
      this.MP4Support = true;
    }
  }
  checkForCredentials() {
    const accessToken = this.camera?.accessToken;
    this.isAuthenticated =
      !!accessToken && accessToken !== 'refresh_token_invalid';
  }

  async submitCredentials(username, password) {
    const { tenantId, camProvider: seller, location } = this.camera;
    const loginCredentials = {
      un: username,
      pw: password,
      tenantId,
      seller,
    };

    try {
      const response = await post(
        `security/openeye_submit_creds/${location}`,
        loginCredentials,
        { orgId: this.orgId },
      );
      if (response === 'Invalid credentials') throw Error(response);

      response.location = location;
      this.camera.accessToken = response.accessToken;
      this.camera.cameraList = response.cameraList;
      this.camera.devices = response.devices;
      this.camera.openeyeHost = response.openeyeHost;
      this.isAuthenticated = true;

      return response;
    } catch (err) {
      console.error(
        'API Error Response:',
        err.response ? err.response.data : err,
      );
      throw new Error(
        'We were unable to authenticate your UAS account. Please try again later.',
      );
    }
  }

  // connection semantics
  async fetchConnectionSemantics() {
    const accessToken = this.camera?.accessToken;
    const device = this.camera?.devices?.[0];

    if (!device || !accessToken) {
      const { browser } = Bowser.parse(window.navigator.userAgent);
      console.error(
        'Missing access token or device information needed to fetch the connection semantics.',
        { device, accessToken, browser },
      );
      throw Error(
        `We were unable to find the cameras associated with your UAS account. ${contactSupportMessage}`,
      );
    }

    if (this.HLSSupport) return this.fetchConnectionSemanticsForHLS();
    if (this.MP4Support) return this.fetchConnectionSemanticsForMP4();

    const { browser } = Bowser.parse(window.navigator.userAgent);
    throw Error(
      `We were unable to connect to the UAS video player using ${browser.name}. ${contactSupportMessage}`,
    );
  }
  async fetchConnectionSemanticsForMP4() {
    const accessToken = this.camera?.accessToken;
    const device = this.camera?.devices?.[0];
    try {
      const data = await get(
        `security/get_login_only/${device.id}/${accessToken}`,
        { orgId: this.orgId },
      );
      return data;
    } catch (err) {
      console.error(err);
      const { browser } = Bowser.parse(window.navigator.userAgent);
      throw Error(
        `We were unable to connect to the UAS video player using ${browser.name}. ${contactSupportMessage}`,
      );
    }
  }
  async fetchConnectionSemanticsForHLS() {
    const accessToken = this.camera?.accessToken;
    const device = this.camera?.devices?.[0];
    const data = await get(
      `security/get_bearer_token_and_device_uri/${device.id}/${accessToken}`,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      const { browser } = Bowser.parse(window.navigator.userAgent);
      throw Error(
        `We were unable to connect to the UAS video player using ${browser.name}. ${contactSupportMessage}`,
      );
    });

    return data;
  }

  // video
  async fetchVideo(cameraId, seekTime) {
    // event.time from alerts is in YYYYMMDDHHmmss format but fetchVideo from other pages are already in YYYY-MM-DD HH:mm:ss,
    // so we need to check the format and convert if necessary to ensure YYYY-MM-DD HH:mm:ss format for the video player
    const formattedTime = /^\d{14}$/.test(this.event.time)
      ? dayjs(this.event.time, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')
      : this.event.time;
    const nowPlaying = {
      seekButtons: true,
      offsetSeconds: 0,
      displayTime: formattedTime,
      controls: true,
      accessToken: this.camera.accessToken,
    };

    const cameraList = this.camera?.cameraList;
    let connectionSemantics;

    try {
      connectionSemantics = await this.fetchConnectionSemantics();
    } catch (err) {
      console.error(
        'Error fetching connection semantics while loading the video player',
      );
      throw Error(err.message);
    }

    try {
      await this.checkCookie();
    } catch (err) {
      console.error('Error checking cookie while loading the video player');
    }

    // If video seek button pressed
    if (seekTime) {
      nowPlaying.offsetSeconds += seekTime;
      nowPlaying.displayTime = dayjs(
        nowPlaying.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      )
        .add(seekTime, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
      this.event.time = nowPlaying.displayTime;
    }

    if (!cameraList || !cameraList[0]) {
      console.error('Missing cameraList:', { cameraList });
      throw Error(
        `We were unable to find the cameras associated with your UAS account. ${contactSupportMessage}`,
      );
    }

    if (cameraId)
      nowPlaying.camera = cameraList.find((camera) => camera.id === cameraId);
    else nowPlaying.camera = cameraList[0];

    if (!nowPlaying?.camera?.id) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList },
      );
      throw Error(
        `We were unable to find the video for this camera because this camera was not found to be associated with your UAS account. ${contactSupportMessage}`,
      );
    }

    nowPlaying.cameraId = nowPlaying.camera.id;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        nowPlaying.camera,
      );
      throw Error(
        `We were unable to identify the selected camera in order to display the associated video. ${contactSupportMessage}`,
      );
    }

    const epochMicroSeconds = epochTimeOfSelectedEvent({
      format: 'micro',
      time: nowPlaying.displayTime,
      timezone: this.camera?.timezone,
    });

    const videoDuration = 30;
    const startTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss');
    const endTime = startTime.add(videoDuration, 'seconds');
    const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
      .add(videoDuration, 'seconds')
      .format('HH:mm:ss');

    nowPlaying.description = `${nowPlaying.camera.name} from ${nowPlaying.displayTime} to ${videoEndTime}`;

    if (this.HLSSupport) {
      const src = await this.fetchHLSVideo(
        connectionSemantics,
        epochMicroSeconds,
        nowPlaying.cameraId,
      );
      nowPlaying.video = {
        sources: [{ src, type: 'application/x-mpegURL' }],
        ...this.commonVideoOptions,
      };
    } else if (this.MP4Support) {
      const src = this.fetchMP4Video(
        connectionSemantics,
        epochMicroSeconds,
        nowPlaying.cameraId,
      );
      nowPlaying.video = {
        sources: [{ src, type: 'video/mp4' }],
        ...this.commonVideoOptions,
      };
    } else throw Error('We were unable to open the UAS video player');

    return nowPlaying;
  }
  async fetchHLSVideo(connectionSemantics, epochMicroSeconds, cameraId) {
    const endMicroSeconds = epochMicroSeconds + 35 * 1000 * 1000;
    const params = {
      time: epochMicroSeconds,
      etime: endMicroSeconds,
      rhttps: connectionSemantics.relaySession,
      atok: connectionSemantics.token,
    };
    const url = `${
      connectionSemantics.deviceUri
    }/stream/hls/newsession/${cameraId}/low?${new URLSearchParams(
      params,
    ).toString()}`;

    const response = await fetch(url, {
      method: 'POST',
    }).catch((err) => {
      console.error(err);
      throw Error(
        `We were unable to reach UAS at this time. Please try again later. If this error is reoccurring, ${contactSupportMessage}`,
      );
    });

    if (response.ok) {
      const body = await response.json();
      const newUrl = `${connectionSemantics.deviceUri}${body.content.url}`;
      return newUrl;
    }

    console.error(`UAS responded with ${response.statusText}`, {
      url,
      response,
    });
    throw Error(
      `UAS was unable to find the video for this event. ${contactSupportMessage}`,
    );
  }

  fetchMP4Video(connectionSemantics, epochMicroSeconds, cameraId) {
    const videoDurationInSeconds = 30;
    const { $PROTOCOL, host } = connectionSemantics;

    if (!$PROTOCOL || !host) throw Error('Missing connection semantics');

    const endMicroSeconds =
      epochMicroSeconds + videoDurationInSeconds * 1000 * 1000;
    const url = `${$PROTOCOL.toLowerCase()}://${host}/stream/mp4/${cameraId}/default/stream.mp4?time=${epochMicroSeconds}&etime=${endMicroSeconds}`;

    return url;
  }

  // thumbnails
  async fetchThumbnails() {
    let thumbnails = [];

    try {
      let connectionSemantics;

      try {
        connectionSemantics = await this.fetchConnectionSemantics();
      } catch (err) {
        console.error(
          'Error fetching connection semantics while loading the video player',
        );
        throw Error(err.message);
      }

      if (this.HLSSupport) {
        // do not call checkCookie for HLS since it is not part of the HLS workflow
        thumbnails = await this.fetchHLSThumbnails(connectionSemantics).catch(
          (err) => {
            throw Error(
              'We were unable to fetch the video thumbnails for this event',
            );
          },
        );
      } else if (this.MP4Support) {
        await this.checkCookie();
        thumbnails = await this.fetchMP4Thumbnails(connectionSemantics).catch(
          (err) => {
            throw Error(
              'We were unable to fetch the video thumbnails for this event',
            );
          },
        );
      } else
        throw Error(
          `We were unable to connect to the UAS video player using ${browser.name}  ${browser.version} ${os.name}.`,
        );
    } catch (err) {
      // just return a list of broken images
      const { cameraList } = this.camera;
      thumbnails = cameraList.map((c) => ({
        src: null,
        title: formatTitle(c.name),
        id: camera.id,
      }));
    }

    return thumbnails;
  }
  async fetchHLSThumbnails(connectionSemantics) {
    const cameraList = this.camera?.cameraList || [];

    const promises = cameraList.map(async (camera) => {
      const microSecondsNow =
        Math.round(new Date().getTime() * 1000) - 60 * 1000 * 1000 * 240;
      const url = `${connectionSemantics.deviceUri}/api/cameras/${camera.id}/default/${microSecondsNow}/120/90/frame`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${connectionSemantics.token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Relay-Session': connectionSemantics.relaySession,
        },
      }).catch((err) => {
        console.error('failed to fetch HLS thumbnails', err);
        throw Error(
          `We were unable to reach the UAS video player at this time. Please try again later. If this error is reoccurring, ${contactSupportMessage}`,
        );
      });

      const image = await response.blob();
      const title = formatTitle(camera.name);
      return { src: URL.createObjectURL(image), title, id: camera.id };
    });
    const thumbnails = await Promise.all(promises);
    return thumbnails;
  }
  async fetchMP4Thumbnails(connectionSemantics) {
    const thumbnails = [];

    const { $PROTOCOL, host } = connectionSemantics;
    const { cameraList } = this.camera;

    await cameraList.forEach(async (camera) => {
      const microSecondsNow =
        Math.round(new Date().getTime() * 1000) - 60 * 1000 * 1000 * 240;

      const image = new Image();
      image.src = `${$PROTOCOL.toLowerCase()}://${host}/apix/cameras/${
        camera.id
      }/default/${microSecondsNow}/120/90/frame`;

      image.onerror = () => {
        const index = thumbnails.findIndex((cam) => cam.id === camera.id);
        console.warn(`${camera.name} thumbnail not found: ${image.src}.`);
        thumbnails[index].src = ''; // setting the src to an empty string allows the video modal to show the no video icon
      };

      thumbnails.push({
        src: image.src,
        title: formatTitle(camera.name),
        id: camera.id,
      });
    });

    return thumbnails;
  }

  /*
   *  OpenEye needs to fetch the first thumbnail with the device login url
   *  in order to set the session cookies, this is an important step for fetching the MP4 thumbnails
   *  but should not be run for HLS thumbnails
   */
  async checkCookie() {
    // set the cookie
    if (!this.cookieSet) {
      if (!this.settingCookie) {
        try {
          this.settingCookie = await this.setCookieByGettingFirstThumbnail();
        } catch (err) {
          this.settingCookie = null;
        }
      }
    }
  }
  async setCookieByGettingFirstThumbnail() {
    const { openeyeHost, accessToken } = this.camera;
    const firstDevice = this.camera.devices[0];
    const uniqueId = firstDevice.cameras[0].uniqueId;
    const id = firstDevice.id;
    if (!uniqueId || !id)
      throw Error('Missing uniqueId needed to set the cookie');
    const microSecondsNow =
      Math.round(new Date().getTime() * 1000) - 60 * 1000 * 1000 * 240;
    const thumbnailUrl = `/apix/cameras/${uniqueId}/default/${microSecondsNow}/120/90/frame`;

    const load = () =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = () => {
          console.error(
            'Cookies not created because the first thumbnail image failed to load.',
            { openeyeHost, id, thumbnailUrl },
          );
          reject(
            new Error(
              `We were unable to reach the UAS video player at this time. Please try again later. If this error is reoccurring, ${contactSupportMessage}`,
            ),
          );
        };
        image.src = `${openeyeHost}/api/device/login/${id}?v=2&rhttpp=${thumbnailUrl}&dlak=${accessToken}`;
      });

    const response = await load();
    this.cookieSet = true;
    this.settingCookie = null;
    return response;
  }
}
